import PreloadedQueryBoundary from '@github-ui/relay-preloaded-query-boundary'
import {LoadingSkeleton} from '@github-ui/skeleton/LoadingSkeleton'
import {Dialog} from '@primer/react/experimental'
import {Suspense, useEffect} from 'react'
import {graphql, type PreloadedQuery, usePreloadedQuery, useQueryLoader} from 'react-relay'

import {LABELS} from '../constants/labels'
import type {EditHistoryDialogQuery} from './__generated__/EditHistoryDialogQuery.graphql'
import {CouldNotFindFallbackError} from './CouldNotFindFallbackError'
import styles from './EditHistoryDialog.module.css'
import {ActorOperationDetails, EditHistoryDialogHeader} from './EditHistoryDialogHeader'
import {GroupedTextDiffViewer} from './GroupedTextDiffViewer'

type EditHistoryDialogProps = {
  userContentEditId: string
  onClose: () => void
}

const EditHistoryContentGraphql = graphql`
  query EditHistoryDialogQuery($id: ID!) {
    node(id: $id) {
      ... on UserContentEdit {
        diff
        diffBefore
        deletedAt
        deletedBy {
          login
        }
        ...EditHistoryDialogHeaderFragment @dangerously_unaliased_fixme
      }
    }
  }
`

export const EditHistoryDialog = ({userContentEditId, onClose}: EditHistoryDialogProps) => {
  return (
    <Dialog
      renderBody={() => <EditContent userContentEditId={userContentEditId} />}
      onClose={onClose}
      title={LABELS.editHistory.viewingEditTitle}
      width="xlarge"
      height="auto"
      className={styles.EditHistoryDialogContainer}
    />
  )
}

const EditContent = ({userContentEditId}: Pick<EditHistoryDialogProps, 'userContentEditId'>) => {
  const [queryRef, loadEditContent] = useQueryLoader<EditHistoryDialogQuery>(EditHistoryContentGraphql)

  useEffect(() => {
    loadEditContent({id: userContentEditId})
  }, [loadEditContent, userContentEditId])

  if (!queryRef) return <EditContentLoading />

  return (
    <Suspense fallback={<EditContentLoading />}>
      <PreloadedQueryBoundary
        onRetry={() => loadEditContent({id: userContentEditId}, {fetchPolicy: 'network-only'})}
        fallback={retry => <CouldNotFindFallbackError retry={retry} />}
      >
        <EditContentInternal queryRef={queryRef} userContentEditId={userContentEditId} />
      </PreloadedQueryBoundary>
    </Suspense>
  )
}

const EditContentInternal = ({
  queryRef,
  userContentEditId,
}: {
  queryRef: PreloadedQuery<EditHistoryDialogQuery>
  userContentEditId: string
}) => {
  const preloadedData = usePreloadedQuery<EditHistoryDialogQuery>(EditHistoryContentGraphql, queryRef)
  const data = preloadedData.node
  if (!data) return <EditContentLoading />

  // This should only be possible to happen on the first creation of an issue body
  const emptyDiff = !data.diffBefore && !data.diff

  return (
    <div className={styles.editHistoryContentScrollable}>
      <EditHistoryDialogHeader queryRef={data} userContentEditId={userContentEditId} />
      <div className={styles.contentViewerContainer}>
        {!data.deletedAt && !emptyDiff && (
          <GroupedTextDiffViewer before={data.diffBefore ?? undefined} after={data.diff ?? undefined} />
        )}
        {!data.deletedAt && emptyDiff && <span className={styles.mutedInfoText}>{LABELS.editHistory.emptyEdit}</span>}
        {data.deletedAt && (
          <ActorOperationDetails
            login={data.deletedBy?.login}
            details={LABELS.editHistory.deletedThisRevision}
            time={data.deletedAt}
            className={styles.mutedInfoText}
          />
        )}
      </div>
    </div>
  )
}

function EditContentLoading() {
  return (
    <div className={styles.loadingSkeletonContainer}>
      <LoadingSkeleton variant="rounded" height="md" width="100%" />
      <LoadingSkeleton variant="rounded" height="md" width="100%" />
    </div>
  )
}

try{ EditHistoryDialog.displayName ||= 'EditHistoryDialog' } catch {}
try{ EditContent.displayName ||= 'EditContent' } catch {}
try{ EditContentInternal.displayName ||= 'EditContentInternal' } catch {}
try{ EditContentLoading.displayName ||= 'EditContentLoading' } catch {}